// C:\Users\cornn\source\repos\purehealthnexus\src\components\AiDashboard\AiMyNutritionStatus\AiUser\AiUserSettings\DeleteAccount.jsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteUser } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

import { useAuth } from '../../../../../firebase/AuthContext';
import { auth, db } from '../../../../../firebase/firebaseConfig';

import './DeleteAccount.css'; // optional, if you want to style this page

const USERS_COLLECTION = 'users';
const ACCOUNT_DELETED_DOC_ID = 'accountDeleted';

// Normalize emails before storing/looking up in Firestore
function normalizeEmail(email) {
  return (email || '').trim().toLowerCase();
}

// Write the deleted email into /users/accountDeleted, along with the userId
async function recordDeletedEmail(email, userId) {
  const normalizedEmail = normalizeEmail(email);

  // If we somehow have neither email nor uid, skip writing
  if (!normalizedEmail && !userId) {
    return;
  }

  const accountDeletedReference = doc(db, USERS_COLLECTION, ACCOUNT_DELETED_DOC_ID);

  await setDoc(
    accountDeletedReference,
    {
      // Keyed by normalized email so we can quickly check at login
      [normalizedEmail]: {
        deletedAt: serverTimestamp(),
        reason: 'user_requested_delete',
        userId: userId || null
      }
    },
    { merge: true }
  );
}

function DeleteAccount() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  function handleBackToBillingClick() {
    navigate('/account/billing');
  }

  function handleOpenConfirmDialog() {
    setErrorMessage('');
    setIsConfirmOpen(true);
  }

  function handleCancelDelete() {
    if (isDeleting) return;
    setIsConfirmOpen(false);
  }

  async function handleConfirmDelete() {
    if (!currentUser) {
      setErrorMessage('You need to be signed in to delete your account.');
      setIsConfirmOpen(false);
      return;
    }

    setIsDeleting(true);
    setErrorMessage('');

    const userEmail = currentUser.email || '';
    const userId = currentUser.uid;

    try {
      // 1. Record that this email (and uid) has deleted their account
      try {
        await recordDeletedEmail(userEmail, userId);
      } catch (recordError) {
        // eslint-disable-next-line no-console
        console.error('Failed to record deleted email:', recordError);
        // We still attempt to delete the auth user even if this fails.
      }

      // 2. Delete the Firebase Authentication user (ONLY auth, not Firestore user doc)
      await deleteUser(auth.currentUser);

      window.location.assign('/');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete account:', error);

      if (error.code === 'auth/requires-recent-login') {
        setErrorMessage(
          'For security reasons, please sign in again and then try deleting your account.'
        );
      } else {
        setErrorMessage('Could not delete your account. Please try again in a moment.');
      }

      setIsConfirmOpen(false);
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="delete-account-page">
      {/* Header */}
      <header className="delete-account-header">
        <h1>Delete Account</h1>
        <p>
          Deleting your account will remove your login from our system. This action cannot be undone.
        </p>
      </header>

      {/* Simple navigation row to match Change Plan | Billing | Delete Account */}
      <nav className="account-settings-navigation">
        <span
          className="account-settings-navigation-item"
          onClick={handleBackToBillingClick}
          role="button"
          tabIndex={0}
        >
          Change Plan
        </span>
        <span className="account-settings-navigation-separator"> | </span>
        <span
          className="account-settings-navigation-item"
          onClick={handleBackToBillingClick}
          role="button"
          tabIndex={0}
        >
          Billing
        </span>
        <span className="account-settings-navigation-separator"> | </span>
        <span className="account-settings-navigation-item account-settings-navigation-item--active">
          Delete Account
        </span>
      </nav>

      {/* Main content */}
      <section className="delete-account-content">
        <p>
          When you delete your account, your login is removed from our authentication system.
          Your existing account data in our database may be retained for billing or compliance purposes.
        </p>

        {errorMessage && (
          <div className="delete-account-error">
            {errorMessage}
          </div>
        )}

        <button
          type="button"
          className="delete-account-button"
          onClick={handleOpenConfirmDialog}
          disabled={isDeleting}
        >
          {isDeleting ? 'Deleting…' : 'Delete my account'}
        </button>
      </section>

      {/* Confirmation popup */}
      {isConfirmOpen && (
        <div className="delete-account-modal-backdrop">
          <div
            className="delete-account-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-modal-title"
          >
            <h2 id="delete-account-modal-title">Are you sure you want to delete your account?</h2>
            <p>
              This will permanently remove your login from our authentication system. Your account
              data may still be kept for billing or legal reasons, but you will not be able to log in again.
            </p>

            <div className="delete-account-modal-actions">
              <button
                type="button"
                className="delete-account-modal-button delete-account-modal-button-cancel"
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delete-account-modal-button delete-account-modal-button-confirm"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Yes, delete my account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeleteAccount;
